import { useEffect, useRef, useState } from "react";
import WrapperCard from "../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../component-lib/Atoms/heading/EventEHRHeading";
import Button from "../../component-lib/Molecules/button/Button";
import Table from "../../component-lib/Organisms/table/EventEHRTable";
import {
  bottomDirectionArrow,
  exportCCDA,
  exportCSV,
  importCSV,
  plusIcon,
  printIcon,
  searchIcon,
} from "../../assets";
import MenuDropdown from "../../component-lib/Molecules/menuDropdown/MenuDropdown";
import useDeviceType from "../../custom-hooks/DeviceType";
import { TEXT_CONSTANTS } from "../../utils/text-constants/textConstants";
import AddContactDirectory from "./dialogs/AddContactDirectory";
import PopoverButton from "../../component-lib/Molecules/popover-button/PopoverButton";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteContact,
  getContactDirectoryList,
  postContactDirectoryData,
  updateContactData,
} from "./contactDirectorySaga";
import {
  componentKey,
  setCloseAddContactDialog,
} from "./contactDirectorySlice";
import { formatPhoneNumber } from "../../utils/phoneNumberFormatter";
import SearchSelectDropdown from "../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import EventEHRConfirmationDialog from "../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";
import TooltipForTable from "../../component-lib/Molecules/tooltip/TooltipForTable";
import { getUserPermission } from "../../utils/getToken";

const ContactDirectory = () => {
  const { closeAddContactDialog, contactDirectoryData } = useSelector(
    (state: any) => state[componentKey]
  );

  const userPermissionData: any = getUserPermission();

  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const [rowPerPage, setRowPerPage] = useState("10");
  const [addNewContact, setAddNewContact] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [defaultIsEditData, setDefaultIsEditData] = useState<any>({
    isEdit: false,
    data: {},
  });
  const [searchedValue, setSearchValue] = useState<string>("");
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(
    null
  );

  //POPOVER BUTTON EVENTS
  const handleImportCSV = () => alert("Import CSV clicked!");
  const handleExportCSV = () => alert("Export CSV clicked!");
  const handleExportCCDA = () => alert("Export CCDA clicked!");
  const handlePrint = () => alert("Print clicked!");

  // CONTACT DIRECTORY LIST COLUMN DATA
  const contactDirectoryTableColumns = [
    {
      field: "name",
      label: <div>Name</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal common-text-ellipse-style">
            {row?.name || "-"}
          </p>
        );
      },
    },
    {
      field: "address",
      label: <div>Address</div>,
      renderLogic: (row: any) => {
        return <TooltipForTable label={row?.address || "-"} />;
      },
    },
    {
      field: "specialties",
      label: <div>Specialties</div>,
      renderLogic: (row: any) => {
        return <TooltipForTable label={row?.specialties || "-"} />;
      },
    },
    {
      field: "contactNumber",
      label: <div>Contact Number</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.contactNumber}</p>
        );
      },
    },
    {
      field: "email",
      label: <div>Email</div>,
      renderLogic: (row: any) => {
        return <TooltipForTable label={row?.email || "-"} />;
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => {
        return (
          <>
            <div className="flex items-center">
              <MenuDropdown
                options={[
                  {
                    label: "Edit Contact",
                    onClickCb: () => {
                      setDefaultIsEditData({ isEdit: true, data: row });
                      setAddNewContact(true);
                    },
                    disabled: !userPermissionData?.contactDirectory?.update, // Disable if permission is false or undefined
                  },
                  {
                    label: "Delete Contact",
                    onClickCb: () => {
                      setSelectedContactId(row?.id);
                      setOpenConfirmationDialog(true);
                    },
                    disabled: !userPermissionData?.contactDirectory?.delete, // Disable if permission is false or undefined
                  },
                ]}
              />
            </div>
          </>
        );
      },
    },
  ];

  // CONTACT DIRECTORY LIST ROW DATA
  const contactDirectoryListRowData = contactDirectoryData?.contact?.map(
    (data: any) => ({
      name: data?.name || "-",
      address: data?.address || "-  ",
      specialties: data?.specialities ? data?.specialities?.join(", ") : "-",
      contactNumber: data?.contactNumber
        ? formatPhoneNumber(data?.contactNumber)
        : "-",
      email: data?.email || "",
      id: data?.id,
    })
  );

  // USING THIS DIALOG WE ARE CLOSE ADD CONTACT DIR. POPUP
  useEffect(() => {
    if (closeAddContactDialog) {
      setAddNewContact(false);
      dispatch(setCloseAddContactDialog(false));

      setOpenConfirmationDialog(false); // for delete contact confirmation dialog(for api failing scenario)
    }

    try {
      const payload = {
        pageNumber: pageNumber,
        limit: rowPerPage,
        searchKey: searchedValue?.trim(),
      };
      dispatch(getContactDirectoryList(payload));
    } catch (error) {
      console.log("error:", error);
    }
  }, [closeAddContactDialog, pageNumber, rowPerPage, searchedValue]);

  // HANDLE SEARCH CHANGE VALUE AND ADDED SOME DELAY
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);

    const value = e.target.value;

    // CLEAR THE PREVIOUS TIMEOUT
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // SET A NEW TIMEOUT TO DELAY THE CALLBACK
    typingTimeoutRef.current = setTimeout(() => {
      setSearchValue(value?.trim());
    }, 500); // ADJUST DELAY AS NEEDED
  };

  // USING THIS METHOD WE ARE ADD NEW CONTACT DIRECTORY
  const handleAddContactClick = (data: any) => {
    const payload = {
      apiPayload: {
        name: data?.hospitalName || undefined,
        address: data?.address || undefined,
        specialities: data?.specialties
          ? data?.specialties?.split(",")
          : `${[data?.specialties]}` || undefined,
        contactNumber: data?.contactNumber || undefined,
        email: data?.email || undefined,
      },
      id: defaultIsEditData?.data?.id,
    };
    if (defaultIsEditData?.isEdit) {
      dispatch(updateContactData(payload));
    } else {
      dispatch(postContactDirectoryData(payload));
    }
  };

  return (
    <WrapperCard
      customClasses="m-3 p-3 flex-1 min-h-0"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col h-full gap-3">
        <div className={`flex items-center justify-between w-full`}>
          <Heading
            title={TEXT_CONSTANTS?.CONTACT_DIRECTORY}
            customStyle={{ color: "#1D1D1D" }}
          />

          <div className={`flex gap-3`}>
            <SearchSelectDropdown
              freeSolo={true}
              searchedValue={searchedValue}
              onSearchValueChangeCb={(e) => handleChange(e)}
              size="small"
              label="Search by name, contact number"
              fullWidth={deviceType === "mobile" ? "160px" : "300px"}
              startIcon={
                <img
                  alt="search-icon"
                  src={searchIcon}
                  width={"18px"}
                  height={"18px"}
                />
              }
            />

            {/* Custom Popover with trigger styles and icons */}
            {/* <PopoverButton
              buttonLabel="Import/Export"
              variant="outlined"
              // startIcon={<ImportExportIcon />}
              endIcon={<img src={bottomDirectionArrow} alt="edit" />}
              buttonStyles={{
                padding: "6px 16px",
                fontSize: "14px",
                fontWeight: 400,
                color: "#727272",
                border: "1px solid #D2D2D2",
                backgroundColor: "#FFFFFF",
                textTransform: "none",
                boxShadow: "none",
              }}
              popoverStyles={{ backgroundColor: "#f9f9f9" }}
              options={[
                {
                  label: "Import\nCSV", // Label split into two lines
                  icon: <img src={importCSV} alt="importIcon" />,
                  onClick: handleImportCSV,
                  // startIcon: <img src={bottomDirectionArrow} alt="edit" />,
                },
                {
                  label: "Export\nCSV",
                  icon: <img src={exportCSV} alt="exportIcon" />,
                  onClick: handleExportCSV,
                },
                {
                  label: "Export\nCCDA",
                  icon: <img src={exportCCDA} alt="exportCCDA" />,
                  onClick: handleExportCCDA,
                },
                {
                  label: "Print",
                  icon: <img src={printIcon} alt="printIcon" />,
                  onClick: handlePrint,
                  disabled: false,
                },
              ]}
            /> */}

            <Button
              disabled={!userPermissionData?.contactDirectory?.create}
              startIcon={
                <img
                  src={plusIcon}
                  alt={"search"}
                  className="cursor-pointer w-[19px] h-[19px]"
                />
              }
              label="Add Contact"
              variant="contained"
              onClickCb={() => {
                setDefaultIsEditData({ isEdit: false, data: {} });
                setAddNewContact(true);
              }}
              style={{
                paddingTop: "6px",
                paddingBottom: "6px",
                minWidth: "155px",
              }}
            ></Button>
          </div>
        </div>
        <div style={{ overflow: "auto" }}>
          <Table
            columns={contactDirectoryTableColumns}
            rows={contactDirectoryListRowData}
            paginationProps={{
              isPagination: true,
              page: pageNumber,
              totalPages: contactDirectoryData?.totalPages,
              totalCount: contactDirectoryData?.total,
              limit: rowPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onPageChange: (page: any) => {
                setPageNumber(page);
              },
              handleChangeRowsPerPage: (rowPerPage: any) => {
                setRowPerPage(rowPerPage);
                setPageNumber(1);
              },
            }}
          />
        </div>
      </div>
      <AddContactDirectory
        isOpen={addNewContact}
        defaultSelectedData={defaultIsEditData}
        label={defaultIsEditData?.isEdit ? "Edit Contact" : "Add Contact"}
        handleClose={() => {
          setAddNewContact(false);
        }}
        handleData={(data) => {
          handleAddContactClick(data);
        }}
      />

      {openConfirmationDialog && (
        <EventEHRConfirmationDialog
          alertTitle="Delete Contact"
          alertDescription="Are you sure you want to delete this contact? Please confirm to proceed."
          cancelButtonTitle="Cancel"
          submitButtonTitle="Delete"
          open={openConfirmationDialog}
          handleClose={(action) => {
            if (action === "close") {
              setOpenConfirmationDialog(false);
            } else if (action === "submit") {
              if (selectedContactId) {
                const payload = {
                  contactId: selectedContactId,
                  comesFrom: "",
                };
                dispatch(deleteContact(payload));
              }
              // setOpenConfirmationDialog(false);
            }
          }}
        />
      )}
    </WrapperCard>
  );
};

export default ContactDirectory;
